import React, { useState } from 'react';
import { sb } from '../supabase';

// Finish email confirmation without leaving the page.
//
// Supabase already issues a numeric code for every confirmation email and hands
// it to our send-email hook as email_data.token — it was simply never shown. The
// hosted project issues 8 digits, though supabase/config.toml still reads
// otp_length = 6; the dashboard is authoritative, which is why nothing here
// assumes a length. The email now prints the code next to the button, and this
// takes it, so signing up no longer means "leave this screen, find an email,
// click a link, come back". That break is where we lost people: the one real
// factory that ever claimed created two accounts three minutes apart and
// confirmed neither, then finished from a different browser entirely.
//
// The link still works. This is an additional way through, not a replacement.

const T = {
  ar: {
    title: 'أدخل الرمز',
    body: (e) => `أرسلنا رمزًا إلى ${e}. أدخله هنا لإكمال التسجيل.`,
    ph: '••••••••',
    verify: 'تأكيد',
    verifying: 'جارٍ التأكيد…',
    resend: 'إعادة الإرسال',
    resent: '✓ أرسلنا رمزًا جديدًا.',
    bad: 'الرمز غير صحيح أو انتهت صلاحيته.',
    short: 'أدخل الرمز كاملًا.',
    hint: 'أو استخدم زر التفعيل في الرسالة.',
  },
  en: {
    title: 'Enter the code',
    body: (e) => `We sent a code to ${e}. Enter it here to finish signing up.`,
    ph: '••••••••',
    verify: 'Confirm',
    verifying: 'Confirming…',
    resend: 'Resend',
    resent: '✓ A new code is on its way.',
    bad: 'That code is wrong or has expired.',
    short: 'Enter the full code.',
    hint: 'Or use the activation button in the email.',
  },
  zh: {
    title: '输入验证码',
    body: (e) => `我们已向 ${e} 发送验证码，请在此输入以完成注册。`,
    ph: '••••••••',
    verify: '确认',
    verifying: '确认中…',
    resend: '重新发送',
    resent: '✓ 新验证码已发送。',
    bad: '验证码错误或已过期。',
    short: '请输入完整验证码。',
    hint: '或使用邮件中的激活按钮。',
  },
};

// Wide enough for any length Supabase is configured to issue.
const MIN_CODE = 4;
const MAX_CODE = 10;

export default function EmailCodeVerify({
  email,
  lang = 'ar',
  type = 'signup',
  onVerified,
  style = {},
}) {
  const t = T[lang] || T.ar;
  const isAr = lang === 'ar';
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const submit = async (e) => {
    e?.preventDefault?.();
    const token = code.replace(/\D/g, '');
    // Deliberately not pinned to a length. The hosted project issues 8 digits
    // while supabase/config.toml still says otp_length = 6 — the dashboard is
    // authoritative — and hardcoding either number makes the real code
    // impossible to type the day it changes. Supabase rejects a wrong code
    // anyway, so a loose bound here costs nothing.
    if (token.length < MIN_CODE) { setErr(t.short); return; }
    setBusy(true); setErr(''); setMsg('');
    // Same call AuthCallback makes for the link, with the typed code instead of
    // the hashed token — so both routes land on one verification path.
    //
    // Two action types, and they do NOT verify interchangeably: a brand-new
    // account's code is issued as 'signup', while an address that already
    // existed gets 'magiclink', which 'email' covers. Which one is in the
    // visitor's inbox depends on how the code was requested — signup, a resend,
    // or signInWithOtp — and this component cannot know. Login.jsx's phone/email
    // signup path already learned this and falls back; doing it here too means
    // the correct code is never refused for being the other kind. A wrong code
    // still fails both attempts, so the fallback costs one request and no safety.
    const first = await sb.auth.verifyOtp({ email, token, type });
    let { data, error } = first;
    if (error || !data?.session) {
      const alt = type === 'signup' ? 'email' : 'signup';
      const retry = await sb.auth.verifyOtp({ email, token, type: alt });
      if (!retry.error && retry.data?.session) { data = retry.data; error = null; }
    }
    setBusy(false);
    if (error || !data?.session) {
      console.error('[EmailCodeVerify] verifyOtp failed:', error?.message || 'no session');
      setErr(t.bad);
      return;
    }
    onVerified?.(data?.session || null, data?.user || null);
  };

  const resend = async () => {
    setBusy(true); setErr(''); setMsg('');
    const { error } = await sb.auth.resend({ type, email });
    setBusy(false);
    if (error) setErr(error.message); else setMsg(t.resent);
  };

  return (
    <form onSubmit={submit} dir={isAr ? 'rtl' : 'ltr'} data-clarity-mask="True" style={{ marginTop: 14, ...style }}>
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{t.title}</div>
      <p style={{ fontSize: 13, lineHeight: 1.8, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
        {t.body(email)}
      </p>
      <input
        value={code}
        onChange={(ev) => { setCode(ev.target.value.replace(/\D/g, '').slice(0, MAX_CODE)); setErr(''); }}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={MAX_CODE}
        placeholder={t.ph}
        aria-label={t.title}
        dir="ltr"
        style={{
          width: '100%', boxSizing: 'border-box', padding: '12px 14px', textAlign: 'center',
          fontSize: 24, letterSpacing: '0.4em', fontWeight: 600,
          fontFamily: "'SFMono-Regular', Consolas, monospace",
          border: `1px solid ${err ? '#c0392b' : 'var(--border-default, rgba(0,0,0,0.18))'}`,
          borderRadius: 10, background: 'var(--bg-raised, #fff)',
        }}
      />
      {err && <p style={{ color: '#c0392b', fontSize: 12.5, margin: '8px 0 0' }}>{err}</p>}
      {msg && <p style={{ color: '#2d7a4f', fontSize: 12.5, margin: '8px 0 0' }}>{msg}</p>}

      <button type="submit" disabled={busy || code.length < MIN_CODE}
        style={{
          width: '100%', marginTop: 12, padding: '12px 18px', borderRadius: 10, border: 'none',
          background: '#1a1814', color: '#fff', fontSize: 14, fontWeight: 600,
          cursor: busy || code.length < MIN_CODE ? 'default' : 'pointer',
          opacity: busy || code.length < MIN_CODE ? 0.5 : 1,
        }}>
        {busy ? t.verifying : t.verify}
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
        <button type="button" onClick={resend} disabled={busy}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, color: 'var(--text-secondary)', textDecoration: 'underline' }}>
          {t.resend}
        </button>
        <span style={{ fontSize: 12, color: 'var(--text-tertiary, rgba(0,0,0,0.4))' }}>{t.hint}</span>
      </div>
    </form>
  );
}
